import { Box, Typography } from '@mui/material'
import { ean13Valide } from '@iobiz/shared'

/**
 * Rendu EAN-13 en SVG, sans aucune dépendance.
 *
 * ⚠️ La CSP de production est en `script-src 'self'` : aucune bibliothèque de
 * CDN ne peut être chargée. Et embarquer une dépendance de plus pour dessiner
 * trente barres serait disproportionné — l'encodage EAN-13 tient en trois
 * tables et vingt lignes.
 *
 * Le SVG plutôt qu'un canvas : il s'imprime net à n'importe quelle taille,
 * ce qui est précisément ce qu'on attend d'une étiquette de rayon.
 *
 * ⚠️ Seul le DESSIN vit ici. La clé de contrôle et la traduction d'un numéro de
 * document en EAN appartiennent à `@iobiz/shared` : le serveur en a besoin
 * aussi, et deux implémentations de la même clé finissent par diverger — le
 * défaut ne se verrait qu'à la première douchette.
 */

// Jeux A, B et C de l'EAN-13. Chaque chiffre vaut 7 modules.
const A = [
  '0001101', '0011001', '0010011', '0111101', '0100011',
  '0110001', '0101111', '0111011', '0110111', '0001011',
]
const B = [
  '0100111', '0110011', '0011011', '0100001', '0011101',
  '0111001', '0000101', '0010001', '0001001', '0010111',
]
const C = [
  '1110010', '1100110', '1101100', '1000010', '1011100',
  '1001110', '1010000', '1000100', '1001000', '1110100',
]

/**
 * Le PREMIER chiffre n'est pas dessiné : il est encodé par l'alternance des
 * jeux A et B sur les six chiffres du groupe de gauche. C'est ce qui permet
 * à un EAN-13 de tenir dans la même largeur qu'un EAN-12.
 */
const PARITE = [
  'AAAAAA', 'AABABB', 'AABBAB', 'AABBBA', 'ABAABB',
  'ABBAAB', 'ABBBAA', 'ABABAB', 'ABABBA', 'ABBABA',
]

/** Suite de modules (0 = clair, 1 = foncé) pour un EAN-13 complet. */
function modules(code: string): string {
  const chiffres = [...code].map(Number)
  const parite = PARITE[chiffres[0]!]!

  let bits = '101' // garde gauche
  for (let i = 1; i <= 6; i++) {
    bits += (parite[i - 1] === 'A' ? A : B)[chiffres[i]!]
  }
  bits += '01010' // garde centrale
  for (let i = 7; i <= 12; i++) bits += C[chiffres[i]!]
  return bits + '101' // garde droite
}

export function Barcode({
  value,
  height = 56,
  moduleWidth = 2,
}: {
  value: string | null | undefined
  height?: number
  moduleWidth?: number
}): JSX.Element | null {
  if (!value) return null

  if (!ean13Valide(value)) {
    // On ne dessine PAS un code invalide : une douchette le lirait de travers
    // ou pas du tout, et l'étiquette imprimée serait inutilisable en rayon.
    return (
      <Typography variant="caption" sx={{ color: 'warning.main' }}>
        {value} — code non conforme à l’EAN-13, aucune étiquette imprimable
      </Typography>
    )
  }

  const bits = modules(value)
  const largeur = bits.length * moduleWidth
  // Les barres de garde descendent plus bas : c'est ce repère visuel qui
  // permet à l'œil — et au lecteur — de délimiter les groupes.
  const gardes = new Set<number>()
  for (const debut of [0, 1, 2, 45, 46, 47, 48, 49, 92, 93, 94]) gardes.add(debut)

  const hauteurTexte = 12

  return (
    <Box
      component="svg"
      role="img"
      aria-label={`Code-barres ${value}`}
      viewBox={`0 0 ${largeur} ${height + hauteurTexte}`}
      sx={{ width: largeur, maxWidth: '100%', height: 'auto', display: 'block' }}
    >
      <rect width={largeur} height={height + hauteurTexte} fill="#fff" />
      {[...bits].map((bit, i) =>
        bit === '1' ? (
          <rect
            key={i}
            x={i * moduleWidth}
            y={0}
            width={moduleWidth}
            height={gardes.has(i) ? height + 5 : height}
            fill="#000"
          />
        ) : null,
      )}
      <text
        x={largeur / 2}
        y={height + hauteurTexte}
        textAnchor="middle"
        fontSize={hauteurTexte}
        fontFamily="monospace"
        fill="#000"
      >
        {value}
      </text>
    </Box>
  )
}

